import { useState, useEffect, useCallback } from "react";
import { Ship, BusFront, ArrowUpRight, ArrowRight, Radio, AlertTriangle } from "lucide-react";
import { Reveal } from "./Reveal";
import { api } from "@/lib/api";

const BUS_URL = "https://www.metroinfo.co.nz/timetables/";

function minutesLabel(m) {
  if (m === 0) return "Leaving now";
  if (m === 1) return "in 1 min";
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60), mm = m % 60;
  return mm ? `in ${h}h ${mm}m` : `in ${h}h`;
}

function delayLabel(d) {
  if (d == null) return null;
  if (d <= 0) return "On time";
  if (d === 1) return "~1 min late";
  return `~${d} min late`;
}

function DirectionCard({ label, sub, data, testid }) {
  const delay = data ? delayLabel(data.delay_min) : null;
  return (
    <div className="bg-background border-2 border-foreground rounded-2xl p-6 md:p-7 shadow-brutal flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="overline text-secondary flex items-center gap-2"><Ship size={13} /> {label}</p>
        {data?.live && (
          <span className="inline-flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full" data-testid={`${testid}-live`}>
            <Radio size={11} className="animate-pulse" /> Live
          </span>
        )}
      </div>
      <p className="font-mono-plex text-[11px] text-foreground/50 mt-1">{sub}</p>
      {!data ? (
        <div className="mt-6 h-16 rounded-lg bg-muted animate-pulse" />
      ) : data.finished ? (
        <div className="mt-6">
          <p className="font-head font-black text-2xl md:text-3xl tracking-tight leading-none">All done for today</p>
          <p className="mt-2 text-foreground/70 text-sm">First sailing tomorrow at <span className="font-semibold">{data.time}</span></p>
        </div>
      ) : (
        <div className="mt-6">
          <p className="font-head font-black text-4xl md:text-5xl tracking-tight leading-none text-secondary" data-testid={`${testid}-countdown`}>
            {minutesLabel(data.minutes_until)}
          </p>
          <p className="mt-2 text-foreground/70">
            Next ferry departs <span className="font-head font-bold">{data.time}</span>
            {data.live && delay && (
              <span className={`ml-2 font-mono-plex text-xs ${data.delay_min > 0 ? "text-[#c25b3a]" : "text-foreground/50"}`} data-testid={`${testid}-delay`}>· {delay}</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FerryTimetable({ onOpenVan }) {
  const [next, setNext] = useState(null);

  const load = useCallback(() => {
    api.get("/ferry/next").then(({ data }) => setNext(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const alerts = next?.alerts || [];

  return (
    <section id="ferry" className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
      <Reveal>
        <p className="overline text-secondary flex items-center gap-2"><Ship size={14} /> Ferry arrivals</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-head font-black tracking-tight leading-[0.95] text-4xl md:text-5xl">
            Diamond Harbour Ferry
          </h2>
          <p className="font-mono-plex text-xs text-foreground/60 max-w-sm">
            Live times for today, both directions · via Metro Christchurch
          </p>
        </div>
      </Reveal>

      {alerts.length > 0 && (
        <Reveal delay={0.05}>
          <div className="mt-8 bg-primary/25 border-2 border-foreground rounded-xl px-5 py-4 flex items-start gap-3" data-testid="ferry-alerts">
            <AlertTriangle size={18} className="text-[#c25b3a] shrink-0 mt-0.5" />
            <div className="space-y-1">
              {alerts.map((a) => (
                <p key={a} className="text-sm font-medium" data-testid="ferry-alert">{a}</p>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.1}>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <DirectionCard
            label="Leaving Lyttelton"
            sub="Lyttelton Jetty → Diamond Harbour"
            data={next?.lyttelton}
            testid="ferry-lyttelton"
          />
          <DirectionCard
            label="Leaving Diamond Harbour"
            sub="Diamond Harbour → Lyttelton Jetty"
            data={next?.diamond_harbour}
            testid="ferry-diamond-harbour"
          />
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a
            href={BUS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bus-timetable-link"
            className="inline-flex items-center gap-2 font-head font-bold text-sm border-2 border-foreground rounded-full px-5 py-3 hover:bg-muted transition-colors"
          >
            <BusFront size={16} /> Lyttelton → Christchurch Bus Interchange (Route 28) <ArrowUpRight size={15} />
          </a>
          <button
            type="button"
            onClick={() => onOpenVan?.()}
            data-testid="ferry-van-cta"
            className="inline-flex items-center gap-2 font-head font-bold text-sm bg-primary text-foreground border-2 border-foreground rounded-full px-5 py-3 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          >
            <BusFront size={16} /> Got the ferry and need a lift? <ArrowRight size={15} />
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <p className="mt-5 font-mono-plex text-[11px] text-foreground/50">
          Live sailing times from Metro Christchurch when a boat is running; scheduled times otherwise. Crossing takes about 10 minutes.
        </p>
      </Reveal>
    </section>
  );
}
